/* eslint-disable camelcase */
import axios from 'axios'
import {LOGGING_BASE_URL} from '../constants'
import {getAppUserAgent} from './userAgent'
import {getUrlParameterByName} from './url'
import {isCampaignSourceFrom} from './campaignSource'
import {Alert} from './alert'

export const defaultParams = {
    ios: {
        os: 'ios',
        source: 'organic',
    },
    android: {
        os: 'android',
        source: 'organic',
    },
    unsupported: {
        os: 'unsupported',
        source: 'organic',
    },
}

export const snakeToCamelCase = (str) =>
    str.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''))

export const camelToSnakeCase = (str) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

export const getCurrentDate = () => {
    // en-CA returns date in format YYYY-MM-DD
    // timeZone: America/Los_Angeles is to ensure the date is in the desired timezone (PST)
    // which makes it easier for reporting since the google subid data comes back in PST
    return new Date().toLocaleDateString('en-CA', {
        timeZone: 'America/Los_Angeles',
    })
}

export const isValidDate = (dateStr) => {
    const regEx = /^\d{4}-\d{2}-\d{2}$/
    if (!dateStr.match(regEx)) return false // Invalid format
    const d = new Date(dateStr)
    const dNum = d.getTime()
    if (!dNum && dNum !== 0) return false // NaN value, Invalid date
    return d.toISOString().slice(0, 10) === dateStr
}

export const isValidCampaign = (campaignId, act) => {
    // campaign is made of only decimal values
    const isValidCampaignId = /^[\d]+$/g.test(campaignId)
    // act has no template string brackets: {}
    const isValidact = !/[{}]+/g.test(act)
    return isValidCampaignId && isValidact
}

export const parsedCampaignFromUrl = () => {
    const ua = getAppUserAgent()
    const campaign = getUrlParameterByName('campaign')
    const defaultCampaign = defaultParams[ua] && defaultParams[ua].campaign
    if (!campaign) {
        return {campaign: defaultCampaign}
    }
    const parsed = campaign.split('_')
    const campaignId = parsed[0]
    let act // click_id (renamed for anonymity)
    let sgc // gclid (renamed for anonymity)
    act = sgc = parsed.slice(1).join('_') || undefined
    if (!isValidCampaign(campaignId, act)) {
        new Alert().sendOnce({campaign})
        return {campaign: defaultCampaign}
    }
    const isCampaignFromGoogle =
        isCampaignSourceFrom('google_search_network') || isCampaignSourceFrom('google_display_network')
    if (isCampaignFromGoogle) {
        return {
            campaign: campaignId,
            // conditionally add act/sgc if it is not undefined
            ...(act && {act}), // click_id (renamed for anonymity)
            ...(sgc && {sgc}), // gclid (renamed for anonymity)
        }
    }
    return {
        campaign: campaignId,
        // conditionally add act if it is not undefined
        ...(act && {act}), // click_id (renamed for anonymity)
    }
}

export const logSgc = async () => {
    const campaignParts = parsedCampaignFromUrl()
    const {sgc} = campaignParts
    if (!sgc) return
    try {
        await axios.post(`${LOGGING_BASE_URL}/sp/sgc`, {sgc})
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
    }
}

export const getAllAppParams = (experimentId = 'app-v1') => {
    const ua = getAppUserAgent()
    const urlParamKeys = [
        'account',
        'adgroup',
        'campaign',
        'date',
        'keyword',
        'source',
        'gclid',
        'fbclid',
        'exoid',
        'tbid',
        'cid',
        'utm_source',
        's1aid',
        's1cid',
        's1agid',
        's1kid',
    ]
    const paramsParsedFromUrl = urlParamKeys.reduce((acc, key) => {
        if (key === 'date') {
            const date = getUrlParameterByName(key)
            // if date is not in the url, or if it's set in the url, but is not valid date, set it to today
            if (!date || !isValidDate(date)) {
                acc[key] = getCurrentDate()
                return acc
            }
        }
        const defaultVal = defaultParams[ua] && defaultParams[ua][key]
        const param = getUrlParameterByName(key) || defaultVal
        if (!(param === undefined) && !(param === null)) {
            acc[snakeToCamelCase(key)] = param
        }
        return acc
    }, {})
    const campaignParams = parsedCampaignFromUrl()
    let allExtensionParams = {
        ...paramsParsedFromUrl,
        os: defaultParams[ua].os,
        variant: experimentId,
    }
    if (campaignParams.campaign) {
        allExtensionParams = {...allExtensionParams, ...campaignParams}
    }
    return allExtensionParams
}
